/** Side-effecting controllers that mutate signals and talk to the API. */
import { api, ApiError, setCsrf } from "./api";
import * as S from "./state";
import type { ComposeMode } from "./state";
import type { FullMessage, MailAddress, RemoteContentDefault, SaveDraftRequest, SendRequest } from "../shared/types";

/** On first load, try to resume an existing session via the cookie. */
export async function bootstrap(): Promise<void> {
  try {
    const info = await api.session();
    setCsrf(info.csrfToken);
    S.userEmail.value = info.email;
    S.isAdmin.value = info.isAdmin ?? false;
    S.defaultDomain.value = info.defaultDomain ?? "";
    S.passwordChangeEnabled.value = info.passwordChangeEnabled ?? false;
    S.passwordMinLength.value = info.passwordMinLength ?? 12;
    S.passwordMaxLength.value = info.passwordMaxLength ?? 64;
    S.remoteContentDefault.value = info.remoteContentDefault ?? "block";
    S.view.value = "app";
    await loadMailboxes();
    await loadMessages();
  } catch {
    S.view.value = "login";
  }
}

export async function login(email: string, password: string): Promise<void> {
  S.signingIn.value = true;
  S.authError.value = null;
  try {
    const info = await api.login({ email, password });
    setCsrf(info.csrfToken);
    S.userEmail.value = info.email;
    S.isAdmin.value = info.isAdmin ?? false;
    S.defaultDomain.value = info.defaultDomain ?? "";
    S.passwordChangeEnabled.value = info.passwordChangeEnabled ?? false;
    S.passwordMinLength.value = info.passwordMinLength ?? 12;
    S.passwordMaxLength.value = info.passwordMaxLength ?? 64;
    S.remoteContentDefault.value = info.remoteContentDefault ?? "block";
    S.view.value = "app";
    await loadMailboxes();
    await loadMessages();
  } catch (e) {
    S.authError.value = e instanceof Error ? e.message : "Login failed";
  } finally {
    S.signingIn.value = false;
  }
}

export async function logout(): Promise<void> {
  try {
    await api.logout();
  } catch {
    // Ignore; we clear local state regardless.
  }
  setCsrf("");
  S.userEmail.value = "";
  S.isAdmin.value = false;
  S.defaultDomain.value = "";
  S.passwordChangeEnabled.value = false;
  S.passwordMinLength.value = 12;
  S.passwordMaxLength.value = 64;
  S.remoteContentDefault.value = "block";
  S.mailboxes.value = [];
  S.messages.value = [];
  S.messagesTotal.value = 0;
  S.selectedUid.value = null;
  S.selectedUids.value = new Set();
  S.currentMailbox.value = "INBOX";
  S.page.value = 1;
  S.composer.value = null;
  S.composerError.value = null;
  S.view.value = "login";
}

/**
 * Persist the account's remote-content default through the API and reflect the
 * SERVER-CONFIRMED value in the signal (never the optimistic input, so the UI
 * stays truthful on failure). Re-throws non-auth failures so `RemoteContentPanel`
 * can surface a local error state (R4-001); a 401 is consumed by `handleAuthError`
 * (drops to login) and never surfaces to the panel.
 */
export async function setRemoteContentDefault(value: RemoteContentDefault): Promise<void> {
  try {
    const res = await api.account.updatePreferences({ remoteContentDefault: value });
    S.remoteContentDefault.value = res.remoteContentDefault;
  } catch (e) {
    if (!handleAuthError(e)) throw e;
  }
}

export async function loadMailboxes(): Promise<void> {
  try {
    S.mailboxes.value = await api.mailboxes.list();
  } catch (e) {
    handleAuthError(e);
  }
}

export async function selectMailbox(path: string): Promise<void> {
  S.mobilePane.value = "list";
  if (path === S.currentMailbox.value) return;
  S.currentMailbox.value = path;
  S.selectedUid.value = null;
  S.fullMessage.value = null;
  S.selectedUids.value = new Set();
  await loadMessages();
}

/** Fresh load of page 1 — used on mailbox switch, refresh, and initial load. */
export async function loadMessages(): Promise<void> {
  S.page.value = 1;
  S.listLoading.value = true;
  S.listError.value = null;
  try {
    const res = await api.messages(S.currentMailbox.value, 1);
    S.messages.value = res.envelopes;
    S.messagesTotal.value = res.total;
  } catch (e) {
    if (!handleAuthError(e)) {
      S.listError.value = e instanceof Error ? e.message : "Failed to load messages";
      S.messages.value = [];
    }
  } finally {
    S.listLoading.value = false;
  }
}

export async function refresh(): Promise<void> {
  await Promise.all([loadMailboxes(), loadMessages()]);
}

/** Appends the next page of messages — driven by the list's scroll-to-bottom handler. */
export async function loadMoreMessages(): Promise<void> {
  if (S.loadingMore.value || S.listLoading.value || !S.hasMoreMessages.value) return;
  const nextPage = S.page.value + 1;
  S.loadingMore.value = true;
  try {
    const res = await api.messages(S.currentMailbox.value, nextPage);
    S.messages.value = [...S.messages.value, ...res.envelopes];
    S.messagesTotal.value = res.total;
    S.page.value = nextPage;
  } catch (e) {
    if (!handleAuthError(e)) {
      S.listError.value = e instanceof Error ? e.message : "Failed to load more messages";
    }
  } finally {
    S.loadingMore.value = false;
  }
}

export async function openMessage(uid: number): Promise<void> {
  S.mobilePane.value = "reader";
  S.selectedUid.value = uid;
  S.showRemote.value = false;
  S.fullMessage.value = null;
  await loadFullMessage(false);
  void markSeenOnOpen(uid);
}

export async function loadFullMessage(remote: boolean): Promise<void> {
  const uid = S.selectedUid.value;
  if (uid == null) return;
  S.messageLoading.value = true;
  S.messageError.value = null;
  try {
    S.fullMessage.value = await api.message(S.currentMailbox.value, uid, remote);
    S.showRemote.value = remote;
  } catch (e) {
    if (!handleAuthError(e)) {
      S.messageError.value = e instanceof Error ? e.message : "Failed to load message";
    }
  } finally {
    S.messageLoading.value = false;
  }
}

export function allowRemoteContent(): void {
  void loadFullMessage(true);
}

/* --------------------------- .eml attachment preview --------------------------- */

/** Clear, non-crashing copy shown for any preview failure (404/415/422/network). */
const EML_PREVIEW_ERROR = "Could not preview this attachment.";

/**
 * Open the nested-message preview for a `.eml`/`message/rfc822` attachment on
 * the currently-open message. Drives the `emlPreview*` signals that back the
 * modal. Any failure resolves to a clear error state — it never throws or
 * disturbs the reader.
 */
export async function openEmlPreview(partId: string): Promise<void> {
  const uid = S.selectedUid.value;
  if (uid == null) return;
  const mailbox = S.currentMailbox.value;
  S.emlPreviewLoading.value = true;
  S.emlPreviewError.value = null;
  S.emlPreview.value = null;
  try {
    S.emlPreview.value = await api.attachmentPreview(mailbox, uid, partId);
  } catch (e) {
    if (!handleAuthError(e)) {
      S.emlPreviewError.value = EML_PREVIEW_ERROR;
    }
  } finally {
    S.emlPreviewLoading.value = false;
  }
}

/** Close the preview modal and reset its signals. */
export function closeEmlPreview(): void {
  S.emlPreview.value = null;
  S.emlPreviewError.value = null;
  S.emlPreviewLoading.value = false;
}

/* -------------------------------- composer ------------------------------- */

function fmtAddr(a: MailAddress): string {
  return a.name ? `${a.name} <${a.address}>` : a.address;
}

function joinAddrs(list: MailAddress[]): string {
  return list.map(fmtAddr).join(", ");
}

/** Parse a comma/semicolon-separated input into bare email addresses. */
export function parseRecipients(input: string): string[] {
  return input
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => {
      // Accept "Name <addr>" and bare "addr"; keep only the address.
      const m = s.match(/<([^>]+)>/);
      return (m ? m[1]! : s).trim();
    })
    .filter(Boolean);
}

function ensureSubjectPrefix(subject: string, prefix: "Re" | "Fwd"): string {
  const s = subject.trim();
  const re = new RegExp(`^${prefix}:`, "i");
  if (re.test(s)) return s;
  // Treat the opposite prefix as already-threaded too (don't double-stack).
  return `${prefix}: ${s || "(no subject)"}`;
}

function quoteHeader(m: FullMessage): string {
  const on = new Date(m.date).toLocaleString();
  const who = m.from[0] ? fmtAddr(m.from[0]) : "someone";
  return `On ${on}, ${who} wrote:`;
}

/** Build an HTML quoted body from the original message. */
function quotedBody(m: FullMessage): string {
  const bodyContent = m.html
    ? m.html
    : (m.text ?? "").replace(/\n/g, "<br>");
  return `<br><br><div style="border-left:3px solid #ccc;padding-left:8px;color:#666">${quoteHeader(m)}<br>${bodyContent}</div>`;
}

function forwardedBody(m: FullMessage): string {
  const bodyContent = m.html
    ? m.html
    : (m.text ?? "").replace(/\n/g, "<br>");
  return [
    "<br><br>",
    '<div style="border-left:3px solid #ccc;padding-left:8px;color:#666">',
    "<b>---------- Forwarded message ----------</b><br>",
    `<b>From:</b> ${m.from[0] ? fmtAddr(m.from[0]) : ""}<br>`,
    `<b>Date:</b> ${new Date(m.date).toLocaleString()}<br>`,
    `<b>Subject:</b> ${m.subject || "(no subject)"}<br>`,
    `<b>To:</b> ${joinAddrs(m.to)}<br>`,
    m.cc.length ? `<b>Cc:</b> ${joinAddrs(m.cc)}<br>` : "",
    "<br>",
    bodyContent,
    "</div>",
  ].join("");
}

/** Build the reply References chain: existing chain + the message's own id. */
function buildReferences(m: FullMessage): string[] {
  const refs = [...m.references];
  if (m.messageId && !refs.includes(m.messageId)) refs.push(m.messageId);
  return refs;
}

export function openCompose(): void {
  S.composerError.value = null;
  S.composerDraftUid.value = null;
  S.composerLastSaved.value = null;
  const draft: S.ComposerDraft = {
    mode: "new",
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    body: "",
    showCc: false,
  };
  S.composer.value = draft;
  S.composerInitial.value = { ...draft };
}

/** Open the composer prefilled as a reply / reply-all / forward. */
export function openComposeFor(mode: ComposeMode): void {
  const m = S.fullMessage.value;
  if (!m || mode === "new") {
    openCompose();
    return;
  }
  S.composerError.value = null;
  S.composerDraftUid.value = null;
  S.composerLastSaved.value = null;
  const me = S.userEmail.value.toLowerCase();

  if (mode === "forward") {
    const draft: S.ComposerDraft = {
      mode,
      to: "",
      cc: "",
      bcc: "",
      subject: ensureSubjectPrefix(m.subject, "Fwd"),
      body: forwardedBody(m),
      inReplyTo: m.messageId ?? undefined,
      references: buildReferences(m),
      showCc: false,
    };
    S.composer.value = draft;
    S.composerInitial.value = { ...draft };
    return;
  }

  // reply / replyAll — prefer Reply-To, else From.
  const replyTarget = m.replyTo.length ? m.replyTo : m.from;
  const to = joinAddrs(replyTarget);

  let cc = "";
  if (mode === "replyAll") {
    // Everyone on To + Cc, minus ourselves and the people already in To.
    const targetAddrs = new Set(replyTarget.map((a) => a.address.toLowerCase()));
    const extras = [...m.to, ...m.cc].filter((a) => {
      const lc = a.address.toLowerCase();
      return lc !== me && !targetAddrs.has(lc);
    });
    cc = joinAddrs(dedupeAddrs(extras));
  }

  const draft: S.ComposerDraft = {
    mode,
    to,
    cc,
    bcc: "",
    subject: ensureSubjectPrefix(m.subject, "Re"),
    body: quotedBody(m),
    inReplyTo: m.messageId ?? undefined,
    references: buildReferences(m),
    showCc: cc.length > 0,
  };
  S.composer.value = draft;
  S.composerInitial.value = { ...draft };
}

function dedupeAddrs(list: MailAddress[]): MailAddress[] {
  const seen = new Set<string>();
  const out: MailAddress[] = [];
  for (const a of list) {
    const k = a.address.toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(a);
  }
  return out;
}

export function closeComposer(): void {
  S.composer.value = null;
  S.composerInitial.value = null;
  S.composerError.value = null;
  S.composerSending.value = false;
  S.composerShowConfirm.value = false;
  clearDraftTimer();
}

/**
 * Safe close: shows a confirmation dialog if the composer has unsaved changes.
 * Does nothing while a save is in flight.
 */
export function closeComposerSafe(): void {
  const draft = S.composer.value;
  if (!draft) {
    closeComposer();
    return;
  }
  // Block close while a save is in flight.
  if (S.composerSaving.value) return;

  if (S.composerDirty.value) {
    S.composerShowConfirm.value = true;
    return;
  }
  closeComposer();
}

/** Action called by the confirmation dialog: discard changes and close. */
export function discardComposer(): void {
  S.composerShowConfirm.value = false;
  closeComposer();
}

/** Action called by the confirmation dialog: save draft then close. */
export async function saveAndCloseComposer(): Promise<void> {
  S.composerShowConfirm.value = false;
  await saveDraft();
  closeComposer();
}

/* ---------------------------- draft autosave ----------------------------- */

let draftSaveTimer: ReturnType<typeof setTimeout> | null = null;

function clearDraftTimer(): void {
  if (draftSaveTimer !== null) {
    clearTimeout(draftSaveTimer);
    draftSaveTimer = null;
  }
}

function scheduleDraftSave(): void {
  clearDraftTimer();
  draftSaveTimer = setTimeout(() => {
    draftSaveTimer = null;
    void saveDraft();
  }, 3000);
}

export function updateComposer(patch: Partial<S.ComposerDraft>): void {
  const cur = S.composer.value;
  if (!cur) return;
  S.composer.value = { ...cur, ...patch };
  scheduleDraftSave();
}

/**
 * Persist the current composer state as an IMAP draft.
 * Gates: null composer, already saving, empty draft, unchanged since last save.
 * Uses POST on first save, PUT on subsequent saves (replace pattern).
 */
export async function saveDraft(): Promise<void> {
  const draft = S.composer.value;
  if (!draft || S.composerSaving.value) return;

  const toList = parseRecipients(draft.to);
  const ccList = parseRecipients(draft.cc);
  const bccList = parseRecipients(draft.bcc);
  const hasRecipient = toList.length > 0 || ccList.length > 0 || bccList.length > 0;
  const subject = draft.subject.trim();
  const body = draft.body.trim();

  // Don't autosave empty drafts.
  if (!hasRecipient && !subject && !body) return;

  // Skip if nothing changed since the last successful save.
  const last = S.composerLastSaved.value;
  if (last && JSON.stringify(draft) === JSON.stringify(last)) return;

  S.composerSaving.value = true;

  const bodyIsHtml = /<[a-z]/i.test(draft.body);
  const req: SaveDraftRequest = {
    to: toList,
    cc: ccList.length ? ccList : undefined,
    bcc: bccList.length ? bccList : undefined,
    subject: draft.subject,
    html: bodyIsHtml ? draft.body : undefined,
    text: bodyIsHtml
      ? draft.body.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim()
      : draft.body,
    inReplyTo: draft.inReplyTo,
    references: draft.references?.length ? draft.references : undefined,
  };

  const existingUid = S.composerDraftUid.value;
  try {
    const res =
      existingUid != null
        ? await api.drafts.update(existingUid, req)
        : await api.drafts.save(req);

    if (res.savedToDrafts) {
      S.composerDraftUid.value = res.uid;
      S.composerLastSaved.value = { ...draft };
    }
  } catch (e) {
    if (!handleAuthError(e)) {
      console.error("[actions] saveDraft failed:", e);
    }
  } finally {
    S.composerSaving.value = false;
  }
}

export async function sendComposer(): Promise<void> {
  const draft = S.composer.value;
  if (!draft || S.composerSending.value) return;

  const to = parseRecipients(draft.to);
  const cc = parseRecipients(draft.cc);
  const bcc = parseRecipients(draft.bcc);

  if (to.length === 0 && cc.length === 0 && bcc.length === 0) {
    S.composerError.value = "Add at least one recipient";
    return;
  }

  const bodyIsHtml = /<[a-z]/i.test(draft.body);
  const req: SendRequest = {
    to,
    cc: cc.length ? cc : undefined,
    bcc: bcc.length ? bcc : undefined,
    subject: draft.subject,
    html: bodyIsHtml ? draft.body : undefined,
    text: bodyIsHtml
      ? draft.body.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim()
      : draft.body,
    inReplyTo: draft.inReplyTo,
    references: draft.references?.length ? draft.references : undefined,
  };

  S.composerSending.value = true;
  S.composerError.value = null;
  try {
    const res = await api.send(req);
    // Clean up the draft if one was saved.
    const draftUid = S.composerDraftUid.value;
    if (draftUid != null) {
      const draftsMbox = S.mailboxes.value.find((mb) => mb.specialUse === "drafts");
      const draftsPath = draftsMbox?.path ?? "Drafts";
      api.deleteMessage(draftsPath, draftUid).catch(() => {
        // Non-critical: silently ignore draft cleanup failure.
      });
      S.composerDraftUid.value = null;
    }
    closeComposer();
    S.composerNotice.value = res.savedToSent
      ? "Message sent"
      : "Message sent (copy not saved to Sent)";
    setTimeout(() => (S.composerNotice.value = null), 4000);
    // If we're viewing Sent, refresh to show the new copy.
    if (S.currentMailboxObj.value?.specialUse === "sent") void loadMessages();
  } catch (e) {
    if (!handleAuthError(e)) {
      S.composerError.value = e instanceof Error ? e.message : "Failed to send";
    }
  } finally {
    S.composerSending.value = false;
  }
}

/** If the server says we are unauthenticated, drop to the login screen. */
function handleAuthError(e: unknown): boolean {
  if (e instanceof ApiError && e.status === 401) {
    void logout();
    return true;
  }
  return false;
}

/* ----------------------------- folder management ----------------------------- */

export async function createFolder(name: string, parentPath?: string): Promise<void> {
  S.folderActionError.value = null;
  try {
    await api.mailboxes.create({ name, parentPath });
    S.folderCreateParent.value = undefined; // close input
    await loadMailboxes();
  } catch (e) {
    if (!handleAuthError(e)) {
      S.folderActionError.value = e instanceof Error ? e.message : "Failed to create folder";
      // Auto-clear after 5 s
      setTimeout(() => { S.folderActionError.value = null; }, 5000);
    }
  }
}

export async function renameFolder(oldPath: string, newPath: string): Promise<void> {
  S.folderActionError.value = null;
  try {
    await api.mailboxes.rename({ oldPath, newPath });
    S.folderEditPath.value = null; // close inline input
    // Update currentMailbox if the renamed folder was active
    if (S.currentMailbox.value === oldPath) {
      S.currentMailbox.value = newPath;
    }
    await loadMailboxes();
  } catch (e) {
    if (!handleAuthError(e)) {
      S.folderActionError.value = e instanceof Error ? e.message : "Failed to rename folder";
      setTimeout(() => { S.folderActionError.value = null; }, 5000);
    }
  }
}

export async function deleteFolder(path: string): Promise<void> {
  S.folderActionError.value = null;
  try {
    await api.mailboxes.delete(path);
    // If the deleted folder was active, navigate to INBOX
    if (S.currentMailbox.value === path) {
      await selectMailbox("INBOX");
    } else {
      await loadMailboxes();
    }
  } catch (e) {
    if (!handleAuthError(e)) {
      S.folderActionError.value = e instanceof Error ? e.message : "Failed to delete folder";
      setTimeout(() => { S.folderActionError.value = null; }, 5000);
    }
  }
}

/* -------------------------------- actions (Phase 4) ----------------------------- */

/**
 * Toggle the \Seen flag of a message. Updates the envelope in place so
 * the list re-renders without a full reload. Also updates the mailbox unread count.
 */
export async function toggleSeen(uid: number): Promise<void> {
  const mailbox = S.currentMailbox.value;
  const env = S.messages.value.find((m) => m.uid === uid);
  if (!env) return;
  try {
    const updated = await api.updateFlags(mailbox, uid, { seen: !env.seen });
    S.messages.value = S.messages.value.map((m) =>
      m.uid === uid ? { ...m, seen: updated.seen } : m,
    );
    if (S.fullMessage.value?.uid === uid) {
      S.fullMessage.value = { ...S.fullMessage.value, seen: updated.seen };
    }
    // Adjust unread count in the sidebar.
    const delta = updated.seen ? -1 : 1;
    S.mailboxes.value = S.mailboxes.value.map((mb) =>
      mb.path === mailbox ? { ...mb, unread: Math.max(0, mb.unread + delta) } : mb,
    );
  } catch (e) {
    if (!handleAuthError(e)) console.error("[actions] toggleSeen failed:", e);
  }
}

/**
 * Toggle the \Flagged (starred) flag of a message.
 */
export async function toggleFlagged(uid: number): Promise<void> {
  const mailbox = S.currentMailbox.value;
  const env = S.messages.value.find((m) => m.uid === uid);
  if (!env) return;
  try {
    const updated = await api.updateFlags(mailbox, uid, { flagged: !env.flagged });
    S.messages.value = S.messages.value.map((m) =>
      m.uid === uid ? { ...m, flagged: updated.flagged } : m,
    );
    if (S.fullMessage.value?.uid === uid) {
      S.fullMessage.value = { ...S.fullMessage.value, flagged: updated.flagged };
    }
  } catch (e) {
    if (!handleAuthError(e)) console.error("[actions] toggleFlagged failed:", e);
  }
}

/**
 * Move a message to a destination mailbox, then refresh the list.
 */
export async function moveMessage(uid: number, destination: string): Promise<void> {
  const mailbox = S.currentMailbox.value;
  try {
    await api.moveMessage(uid, mailbox, destination);
    // Remove from current list.
    S.messages.value = S.messages.value.filter((m) => m.uid !== uid);
    S.messagesTotal.value = Math.max(0, S.messagesTotal.value - 1);
    if (S.selectedUid.value === uid) {
      S.selectedUid.value = null;
      S.fullMessage.value = null;
    }
    S.selectedUids.value = new Set([...S.selectedUids.value].filter((id) => id !== uid));
    // Refresh mailbox counts.
    await loadMailboxes();
  } catch (e) {
    if (!handleAuthError(e)) console.error("[actions] moveMessage failed:", e);
  }
}

/**
 * Delete a message (moves to Trash; expunges if already in Trash).
 */
export async function deleteMessage(uid: number): Promise<void> {
  const mailbox = S.currentMailbox.value;
  try {
    await api.deleteMessage(mailbox, uid);
    S.messages.value = S.messages.value.filter((m) => m.uid !== uid);
    S.messagesTotal.value = Math.max(0, S.messagesTotal.value - 1);
    if (S.selectedUid.value === uid) {
      S.selectedUid.value = null;
      S.fullMessage.value = null;
    }
    S.selectedUids.value = new Set([...S.selectedUids.value].filter((id) => id !== uid));
    await loadMailboxes();
  } catch (e) {
    if (!handleAuthError(e)) console.error("[actions] deleteMessage failed:", e);
  }
}

/** Mark seen automatically when a message is opened (called by openMessage). */
export async function markSeenOnOpen(uid: number): Promise<void> {
  const mailbox = S.currentMailbox.value;
  const env = S.messages.value.find((m) => m.uid === uid);
  if (!env || env.seen) return; // already seen, skip
  try {
    await api.updateFlags(mailbox, uid, { seen: true });
    S.messages.value = S.messages.value.map((m) =>
      m.uid === uid ? { ...m, seen: true } : m,
    );
    S.mailboxes.value = S.mailboxes.value.map((mb) =>
      mb.path === mailbox ? { ...mb, unread: Math.max(0, mb.unread - 1) } : mb,
    );
  } catch {
    // Non-fatal: silently ignore
  }
}

/** Bulk: move all selected UIDs to destination. */
export async function bulkMove(destination: string): Promise<void> {
  const uids = [...S.selectedUids.value];
  if (uids.length === 0) return;
  S.selectedUids.value = new Set();
  await Promise.all(uids.map((uid) => moveMessage(uid, destination)));
}

/** Bulk: delete all selected UIDs. */
export async function bulkDelete(): Promise<void> {
  const uids = [...S.selectedUids.value];
  if (uids.length === 0) return;
  S.selectedUids.value = new Set();
  await Promise.all(uids.map((uid) => deleteMessage(uid)));
}

/** Bulk: mark all selected as seen or unseen. */
export async function bulkMarkSeen(seen: boolean): Promise<void> {
  const uids = [...S.selectedUids.value];
  if (uids.length === 0) return;
  const mailbox = S.currentMailbox.value;
  await Promise.all(
    uids.map(async (uid) => {
      try {
        const updated = await api.updateFlags(mailbox, uid, { seen });
        S.messages.value = S.messages.value.map((m) =>
          m.uid === uid ? { ...m, seen: updated.seen } : m,
        );
      } catch {
        /* skip */
      }
    }),
  );
  await loadMailboxes();
}

/** Run a search in the current mailbox. Sets searchResults signal. */
export async function runSearch(query: string): Promise<void> {
  const q = query.trim();
  S.searchQuery.value = q;
  if (!q) {
    S.searchResults.value = null;
    return;
  }
  S.searchLoading.value = true;
  S.searchError.value = null;
  try {
    const res = await api.search(S.currentMailbox.value, q);
    S.searchResults.value = res.envelopes;
  } catch (e) {
    if (!handleAuthError(e)) {
      S.searchError.value = e instanceof Error ? e.message : "Search failed";
      S.searchResults.value = [];
    }
  } finally {
    S.searchLoading.value = false;
  }
}

/** Clear search and return to normal list view. */
export function clearSearch(): void {
  S.searchQuery.value = "";
  S.searchResults.value = null;
  S.searchError.value = null;
}

/** Toggle a UID in the bulk-select set. */
export function toggleSelect(uid: number): void {
  const cur = new Set(S.selectedUids.value);
  if (cur.has(uid)) cur.delete(uid);
  else cur.add(uid);
  S.selectedUids.value = cur;
}

/** Select all visible messages. */
export function selectAll(): void {
  S.selectedUids.value = new Set(S.visibleMessages.value.map((m) => m.uid));
}

/** Clear selection. */
export function clearSelection(): void {
  S.selectedUids.value = new Set();
}
